import { useAppDispatch } from '~/store/appDispatch.ts';
import { useSelector } from 'react-redux';
import {
  selectApplicationId,
  selectGeneralInfoLegalResident,
} from '~/store/identification-form/selectors.ts';
import { FormProvider } from 'react-hook-form';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import {
  IdentificationFormStepNames,
  IdentificationFormSteps,
} from '~/modules/identification/types.ts';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices.ts';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '~/modules/identification/components/SetDirtyModalValues.tsx';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { useFatcaAdditionalInformationForm } from '~/modules/identification/forms/FatcaAdditionalInformation/hooks/useFatcaAdditionalInformationForm.ts';
import { TFatcaAdditionalInformation } from '~/modules/identification/forms/FatcaAdditionalInformation/types.ts';
import { styles } from '~/modules/identification/styles.ts';
import { UsEntityInfo } from '~/modules/identification/forms/FatcaAdditionalInformation/components/UsEntityInfo.tsx';
import { ForeignFinancialInstitutionInfo } from '~/modules/identification/forms/FatcaAdditionalInformation/components/ForeignFinancialInstitutionInfo.tsx';
import { NffeInfo } from '~/modules/identification/forms/FatcaAdditionalInformation/components/NffeInfo.tsx';
import { getFatcaLegalResidentValues } from '~/modules/identification/forms/FatcaAdditionalInformation/utils.ts';

interface IFatcaAdditionalInformationProps {
  onNextStep: () => void;
}

export default function FatcaAdditionalInformation({
  onNextStep,
}: IFatcaAdditionalInformationProps) {
  const dispatch = useAppDispatch();
  const appId = useSelector(selectApplicationId);
  const { isUSTaxResident, isForeignFinancialUnderFATCA, isControllingPersonUSTaxResident } =
    useSelector(selectGeneralInfoLegalResident) || {};

  const form = useFatcaAdditionalInformationForm();
  const { isDirty, isSubmitting } = form.formState;

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TFatcaAdditionalInformation) => {
      if (isDirty) {
        try {
          const fatcaAdditionalInformationValue = getFatcaLegalResidentValues(
            values as TFatcaAdditionalInformation as TFatcaAdditionalInformation,
            isUSTaxResident,
            isForeignFinancialUnderFATCA,
            isControllingPersonUSTaxResident
          );

          const data = {
            fatcaAdditionalInformation: fatcaAdditionalInformationValue,
            applicationId: appId as number,
          };
          await dispatch(
            updateForm({
              data,
              reportId: IdentificationFormSteps.fatcaAdditionalInformation,
            })
          );
          const formData = {
            field:
              IdentificationFormStepNames.fatcaAdditionalInformation as keyof TIdentificationForm,
            data: fatcaAdditionalInformationValue,
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        <Typography variant="h3" sx={styles.titleForm}>
          Податкове резидентство (FATCA)
        </Typography>
        {isUSTaxResident && <UsEntityInfo />}
        {isForeignFinancialUnderFATCA && (
          <>
            <Typography variant="h6" sx={styles.titleForm}>
              FATCA-статус іноземної фінансової установи
            </Typography>
            <ForeignFinancialInstitutionInfo />
          </>
        )}
        {isControllingPersonUSTaxResident && (
          <>
            {(isUSTaxResident || isForeignFinancialUnderFATCA) && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <Divider sx={styles.divider} />
              </Box>
            )}
            <Typography variant="h5" sx={styles.titleForm}>
              FATCA-статус установи, контролюючих осіб та/або власників істотної участі
            </Typography>
            <NffeInfo />
          </>
        )}
        <Grid size={12} sx={{ justifyContent: 'center', display: 'flex', paddingTop: 2 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Продовжити
          </Button>
        </Grid>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
      </Box>
      {isSubmitting && <FullPageLoader />}
    </FormProvider>
  );
}
